import { useMemo, useState } from 'react';

import type {
  CourseRegistrationManagementAttachmentGroupViewModel,
  CourseRegistrationManagementDetailsViewModel,
} from '@/shared/api/generated';

import type {
  DocumentationStatus,
  Enrollment,
  EnrollmentDocument,
  EnrollmentStatus,
} from '../types/enrollment';
import { ENROLLMENT_CODE_TO_STATUS } from '../types/enrollmentStatusCode';
import { useEnrollmentDetails } from './useEnrollmentDetails';
import { useManagementStatus } from './useManagementStatus';

const DOCUMENTATION_CODE_TO_STATUS: Record<number, DocumentationStatus> = {
  0: 'pending',
  1: 'approved',
  2: 'rejected',
  3: 'approved_temporary',
};

export function attachmentGroupsToDocuments(
  groups: readonly CourseRegistrationManagementAttachmentGroupViewModel[] | null | undefined,
): readonly EnrollmentDocument[] {
  if (!groups) return [];
  return groups.map((group, index): EnrollmentDocument => {
    const hasFiles = (group.files?.length ?? 0) > 0;
    const mappedStatus =
      group.documentationStatus !== undefined
        ? DOCUMENTATION_CODE_TO_STATUS[group.documentationStatus]
        : undefined;
    const status: DocumentationStatus = hasFiles ? (mappedStatus ?? 'pending') : 'not_sent';
    const files = group.files ?? [];
    const defaultFile = files.length > 1 ? (files.find(f => !f.default) ?? files[0]) : files[0];

    return {
      id: group.enrollmentDocumentId ?? index,
      attachmentId: defaultFile?.id ?? null,
      type: group.documentAttachmentTypeText ?? '',
      fileName: defaultFile?.fileName ?? '',
      fileUrl: '',
      status,
      uploadedAt: defaultFile?.date ?? '',
      approvedBy: group.approvedByPersonName ?? null,
      approvedAt: group.aprrovedDate ?? null,
      rejectionReason: group.rejectReason ?? null,
    };
  });
}

/**
 * Constrói um {@link Enrollment} a partir do payload do endpoint `details-tab`.
 * Campos não cobertos por esse endpoint recebem defaults seguros até que as
 * demais abas sejam migradas para seus respectivos endpoints reais.
 */
function detailsToEnrollment(details: CourseRegistrationManagementDetailsViewModel): Enrollment {
  const status: EnrollmentStatus =
    (details.enrollmentStatus !== undefined &&
      ENROLLMENT_CODE_TO_STATUS[details.enrollmentStatus]) ||
    'payment_selection';
  const documentationStatus: DocumentationStatus =
    (details.documentationStatus !== undefined &&
      DOCUMENTATION_CODE_TO_STATUS[details.documentationStatus]) ||
    'pending';

  return {
    id: details.id ?? 0,
    personId: details.personId ?? 0,
    personName: details.personFullName ?? '',
    personEmail: details.personEmail ?? '',
    personCpf: details.cpf ?? '',
    courseId: details.courseId ?? 0,
    courseName: details.courseName ?? '',
    classId: details.classStudentId ?? 0,
    className: details.classStudentName ?? '',
    poleId: details.poloId ?? null,
    poleName: details.poloName ?? null,
    status,
    documentationStatus,
    scholarshipStatus: 'none',
    enrollmentDate: details.creationDate ?? '',
    startDate: '',
    endDate: '',
    grade: null,
    attendance: null,
    progressPercentage: null,
    isForeign: details.isForeign ?? false,
    foreignStatus: null,
    isPcd: details.isPCD ?? false,
    observation: details.studentNote ?? null,
    statusReason: details.lastStatusReasonName ?? null,
    createdAt: details.creationDate ?? '',
    personAlias: details.personAlias ?? null,
    personRg: details.rg ?? null,
    personPassaporte: details.passport ?? null,
    personPhoneMobile: details.cellPhone ?? null,
    personPhoneWork: details.workPhone ?? null,
    personPhoneHome: details.homePhone ?? null,
    personAddress: details.address ?? null,
    documents: attachmentGroupsToDocuments(details.attachments),
    history: [],
    poloChanges: [],
    courseAccess: { isReleased: false, releasedUntil: null, releasedDays: null },
    scholarships: [],
    selectiveProcess: null,
    auditTrail: [],
    financialSummary: null,
    scholarshipFull: null,
    creationMeta: null,
    lastChangeMeta: null,
    auditLog: [],
  };
}

/**
 * Hook para carregar uma matrícula a partir do endpoint real de Details.
 * Retorna o `Enrollment` enriquecido com defaults para abas ainda não
 * migradas para seus endpoints específicos.
 */
export function useEnrollmentDetail(enrollmentId: number) {
  const { details, isLoading, isError, isRefetching, refetch } = useEnrollmentDetails(enrollmentId);

  const enrollment = useMemo(() => (details ? detailsToEnrollment(details) : undefined), [details]);

  return { enrollment, isLoading, isError, isRefetching, refetch } as const;
}

export function useEnrollmentManagement(enrollmentId: number) {
  const { enrollment, isLoading, isError, isRefetching, refetch } =
    useEnrollmentDetail(enrollmentId);

  const [statusForm, setStatusForm] = useState({
    newStatus: '' as EnrollmentStatus | '',
    reasonId: '',
    subReasonId: '',
    observation: '',
    sendNotification: true,
    sendCertificateByEmail: false,
    updateCompanyDiscounts: false,
  });

  const updateStatusForm = <K extends keyof typeof statusForm>(
    key: K,
    value: (typeof statusForm)[K],
  ) => {
    setStatusForm(prev => ({ ...prev, [key]: value }));
  };

  const [courseAccessForm, setCourseAccessForm] = useState({
    isReleased: enrollment?.courseAccess.isReleased ?? false,
    releasedUntil: enrollment?.courseAccess.releasedUntil ?? '',
    releasedDays: enrollment?.courseAccess.releasedDays ?? 0,
  });

  const updateCourseAccess = <K extends keyof typeof courseAccessForm>(
    key: K,
    value: (typeof courseAccessForm)[K],
  ) => {
    setCourseAccessForm(prev => ({ ...prev, [key]: value }));
  };

  const [docStatus, setDocStatus] = useState<DocumentationStatus>(
    enrollment?.documentationStatus ?? 'pending',
  );

  const resetStatusForm = () => {
    setStatusForm({
      newStatus: '',
      reasonId: '',
      subReasonId: '',
      observation: '',
      sendNotification: true,
      sendCertificateByEmail: false,
      updateCompanyDiscounts: false,
    });
  };

  const { changeStatus: changeStatusMutation, isSaving: isStatusSaving } = useManagementStatus(
    enrollmentId,
    resetStatusForm,
  );

  const changeStatus = () => {
    if (!enrollment) return;
    changeStatusMutation(statusForm, enrollment);
  };

  const saveCourseAccess = () => {};

  const changeDocumentationStatus = (status: DocumentationStatus) => {
    setDocStatus(status);
  };

  return {
    enrollment,
    isLoading,
    isError,
    isRefetching,
    refetch,
    statusForm,
    updateStatusForm,
    resetStatusForm,
    changeStatus,
    isStatusSaving,
    courseAccessForm,
    updateCourseAccess,
    saveCourseAccess,
    docStatus,
    changeDocumentationStatus,
  } as const;
}
